import { useEffect, useMemo, useState } from "react"
import { HeadingXSmall, ModalBody, ModalHeader } from "@mezo-org/mezo-clay"
import { useCopyToClipboard } from "usehooks-ts"

import {
  claimRedemptionCodeForOrder,
  RootState,
  selectMarketProductByTransactionHash,
  selectModalOptions,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"
import { FRIENDLY_DECIMALS_AMOUNT, links } from "#/shared/constants"
import { useMarketProductDataByTransactionHash } from "#/shared/hooks"
import { bigIntToFloat } from "#/shared/utils"
import { BaseModal, BaseModalProps } from "#/ui/Modal"

import MarketTransactionDetailsContent from "./MarketTransactionDetailsContent"
import MarketTransactionDetailsHeader from "./MarketTransactionDetailsHeader"
import { MarketTransactionDetailsItemProps } from "./MarketTransactionDetailsItem"

type TransactionDetails = MarketTransactionDetailsItemProps[]

export default function MarketTransactionDetailsModal({
  isOpen,
  onClose,
}: BaseModalProps) {
  // @ts-expect-error typings has to be improved
  const { transactionHash } = useDappSelector(selectModalOptions)

  const selectedProduct = useDappSelector((state: RootState) =>
    selectMarketProductByTransactionHash(state, transactionHash),
  )

  const productData = useMarketProductDataByTransactionHash(transactionHash)

  const transactionDate = new Date(
    Number(selectedProduct?.timestamp) * 1000,
  ).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  })

  const friendlyDonationAmount = useMemo(() => {
    let amount: bigint
    if (selectedProduct) {
      amount =
        selectedProduct.type === "donation"
          ? BigInt(selectedProduct.amount)
          : BigInt(selectedProduct.price)
      return `${bigIntToFloat(amount, 18, FRIENDLY_DECIMALS_AMOUNT)} MUSD`
    }
    return null
  }, [selectedProduct])

  const [, handleCopy] = useCopyToClipboard()

  const dispatch = useDappDispatch()

  const [redemptionCode, setRedemptionCode] = useState<string | null>()

  useEffect(() => {
    if (!selectedProduct || !("orderId" in selectedProduct)) return

    const handleClaimRedemptionCodeForOrder = async () => {
      const { payload } = await dispatch(
        claimRedemptionCodeForOrder({
          orderId: selectedProduct.orderId,
        }),
      )

      setRedemptionCode(payload as string)
    }

    handleClaimRedemptionCodeForOrder()

    // eslint-disable-next-line consistent-return
    return () => {
      setRedemptionCode(undefined)
    }
  }, [dispatch, selectedProduct, transactionHash])

  const transactionDetails = useMemo(() => {
    let details: TransactionDetails = []

    if (!selectedProduct || !friendlyDonationAmount) return []

    if (selectedProduct.type === "donation") {
      details = [
        {
          label: "Amount",
          value: friendlyDonationAmount,
          type: "text",
        },
      ]
    }

    if (selectedProduct.type === "order") {
      let redeemLink: string | undefined
      if (productData?.id.includes("bitrefill") && redemptionCode) {
        redeemLink = `${links.BITREFILL}/redeem#${redemptionCode}`
      }

      if (
        productData?.id.includes("ledger") &&
        "productRedeemLink" in productData
      ) {
        redeemLink = productData.productRedeemLink
      }

      const friendlyLink = redeemLink
        ? new URL(redeemLink).hostname.replace(/^www\./, "")
        : undefined

      details = [
        {
          label: "Where to redeem",
          value: friendlyLink,
          type: "info-link",
          onClick: () => {
            if (redeemLink) window.open(redeemLink, "_blank")
          },
        },
        {
          label: "Redemption code",
          value: redemptionCode,
          type: "clipboard",
          onClick: () => {
            handleCopy(redemptionCode ?? "")
          },
        },
        {
          label: "Price",
          value: friendlyDonationAmount,
          type: "text",
        },
      ]
    }

    return [
      {
        label: "Transaction ID",
        value: `${selectedProduct.transactionHash.slice(
          0,
          5,
        )}...${selectedProduct.transactionHash.slice(-5)}`.toLowerCase(),
        type: "external-link",
        onClick: () => {
          window.open(
            `${links.BLOCK_EXPLORER}/tx/${selectedProduct.transactionHash}`,
            "_blank",
          )
        },
      },
      ...details,
      {
        label: "Date",
        value: transactionDate,
        type: "text",
      },
    ]
  }, [
    transactionDate,
    friendlyDonationAmount,
    handleCopy,
    selectedProduct,
    productData,
    redemptionCode,
  ])

  if (!productData || !selectedProduct) return null

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      closeable
      overrides={{
        Dialog: {
          style: () => ({
            height: "auto",
          }),
        },
      }}
    >
      <ModalHeader
        style={{ margin: "0px auto", padding: "20px", paddingBottom: 0 }}
      >
        <HeadingXSmall>Receipt</HeadingXSmall>
      </ModalHeader>
      <ModalBody $style={{ marginLeft: "0px", marginRight: "0px" }}>
        <MarketTransactionDetailsHeader
          name={productData.name}
          description={productData.thumbnailDescription}
          image={productData.image}
        />
        <MarketTransactionDetailsContent
          details={transactionDetails as TransactionDetails}
        />
      </ModalBody>
    </BaseModal>
  )
}
